import { HexColorPicker } from "react-colorful";
import { useLocation, useNavigate } from "react-router-dom";
import { useApi } from "../../utils/Api";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  InputLabel,
  Slider,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import Translate from "../../utils/Translate";
import { useState } from "react";
import { useEffect } from "react";
import Preloader from "../Preloader";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { accessKeys, hasAccess } from "../../utils/userAccess";
import { ExpandMore, Info } from "@mui/icons-material";
import MarkdownEditor from "../MarkdownEditor";

const _marks = [
  {
    value: 0,
    label: "0%",
  },
  {
    value: 20,
    label: "20%",
  },
  {
    value: 40,
    label: "40%",
  },
  {
    value: 60,
    label: "60%",
  },
  {
    value: 80,
    label: "80%",
  },
  {
    value: 100,
    label: "100%",
  },
];

function percentToHex(percentage) {
  if (percentage < 0 || percentage > 100) {
    throw new Error("Percentage must be between 0 and 100");
  }

  const decimalValue = Math.round((percentage / 100) * 255);
  let hexValue = decimalValue.toString(16).toUpperCase();
  if (hexValue.length === 1) {
    hexValue = "0" + hexValue;
  }
  return hexValue;
}

function hexToPercent(hex) {
  if (!/^[0-9A-Fa-f]{2}$/.test(hex)) {
    throw new Error(`Hex value must be a valid two-character hexadecimal value, not: ${hex}`);
  }

  const decimalValue = parseInt(hex, 16);
  const percentage = Math.round((decimalValue / 255) * 100 * 100) / 100;
  return percentage;
}

function getColor(colorAndOpacity) {
  return colorAndOpacity?.substring(0, 7);
}

function getOpacity(colorAndOpacity) {
  return !!colorAndOpacity && colorAndOpacity.length === 9 ? colorAndOpacity.substring(7) : null;
}

function ColorPicker({ title, colorName, licenseGroup, handleChangeColor, containerProps, hideTransparency }) {
  const currentColorAndOpacity = licenseGroup[colorName];
  const currentColor = getColor(currentColorAndOpacity);
  const currentOpacity = getOpacity(currentColorAndOpacity);
  return (
    <Stack direction="column" spacing={1} {...containerProps}>
      <InputLabel required>{title}</InputLabel>
      <HexColorPicker color={currentColor} onChange={(color) => handleChangeColor(colorName, color)} />
      <TextField
        sx={{ width: "200px" }}
        value={currentColor ?? ""}
        onChange={(ev) => handleChangeColor(colorName, ev.target.value)}
      />
      {!hideTransparency && (
        <Slider
          sx={{ width: "200px" }}
          value={currentColorAndOpacity && currentColorAndOpacity.length === 9 ? hexToPercent(currentOpacity) : 100}
          step={1}
          valueLabelDisplay="auto"
          marks={_marks}
          onChange={(_, newValue) => handleChangeColor(colorName, null, percentToHex(newValue))}
        />
      )}
    </Stack>
  );
}

export default function AddOrEditLicenseGroupDialog({ licenseGroupId }) {
  const [licenseGroup, setLicenseGroup] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setIsLoading(true);
    const url = licenseGroupId
      ? `${import.meta.env.VITE_MAIN_URL}licenses/groups/${licenseGroupId}`
      : `${import.meta.env.VITE_MAIN_URL}licenses/groups/default`;
    api
      .fetch(url, false, "GET")
      .then((response) => {
        if (response.isSuccessful) {
          setLicenseGroup(response);
        }
      })
      .finally(() => setIsLoading(false));
  }, [api, licenseGroupId]);

  const closeAndReload = () => {
    //Soft reload with redirect
    const hash = window.location.hash.endsWith("#dialog")
      ? window.location.hash.substring(0, window.location.hash.length - 7)
      : window.location.hash;
    const loc = encodeURIComponent(`${location.pathname}${location.search}${hash}`);
    navigate(`/redirect/${loc}`, { replace: true });
  };

  async function postLicenseGroup() {
    const response = await api.fetch(`${import.meta.env.VITE_MAIN_URL}licenses/groups`, licenseGroup, "POST");
    return response.isSuccessful;
  }

  async function handleSaveLicenseGroup() {
    if (await postLicenseGroup()) {
      closeAndReload();
    }
  }

  function handleChangeColor(colorName, newColor, newOpacity) {
    if (newColor && newColor.length != 7) throw new Error("Expected color without opacity");
    const currentColorAndOpacity = licenseGroup[colorName];
    const currentColor = getColor(currentColorAndOpacity);
    const currentOpacity = getOpacity(currentColorAndOpacity);
    const newColorAndOpacity = (newColor ?? currentColor) + (newOpacity ?? currentOpacity ?? "");
    setLicenseGroup((prev) => ({ ...prev, [colorName]: newColorAndOpacity }));
  }

  return (
    <>
      {isLoading && <Preloader />}
      {!isLoading && (
        <>
          <DialogContent className="stdDialogContent dialogOverlay">
            <br />
            <Stack direction="column" gap={4}>
              <TextField
                fullWidth
                value={licenseGroup?.name ?? ""}
                onChange={(event) => setLicenseGroup({ ...licenseGroup, name: event.target.value })}
                label={Translate.get("LicenseName")}
                placeholder={Translate.get("LicenseName")}
                slotProps={{htmlInput: {maxLength: 200}}}
              />
              <TextField
                fullWidth
                value={licenseGroup?.regulation ?? ""}
                onChange={(event) => setLicenseGroup({ ...licenseGroup, regulation: event.target.value })}
                label={Translate.get("LicenseRegulation")}
                placeholder={Translate.get("LicenseRegulation")}
                slotProps={{htmlInput: {maxLength: 20}}}
              />
              <ColorPicker
                title={Translate.get("Color")}
                colorName="webColor"
                licenseGroup={licenseGroup}
                handleChangeColor={handleChangeColor}
                hideTransparency
              />
              {hasAccess(accessKeys.digitalLicenses) && (
                <Accordion className="noPaperMargin noTopBorder" sx={{ padding: 0 }} disableGutters>
                  <AccordionSummary sx={{ padding: 0 }} expandIcon={<ExpandMore />}>
                    <div className="smallHeader">{Translate.get("DigitalLicensesTitle")}</div>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: 0, paddingTop: "12px" }}>
                    <Stack direction="column" gap={2}>
                      <MarkdownEditor
                        label={Translate.get("GenericCourseDescriptionTitle")}
                        placeholder={Translate.get("GenericCourseDescriptionTitle")}
                        markdown={licenseGroup?.fallbackCourseDescription ?? ""}
                        onChange={(markdown) => {
                          const fallbackCourseDescription = markdown.trim() ? markdown : "";
                          const fallbackOnlyShowCourseDescription = !fallbackCourseDescription
                            ? false
                            : licenseGroup.fallbackOnlyShowCourseDescription;
                          setLicenseGroup({
                            ...licenseGroup,
                            fallbackCourseDescription,
                            fallbackOnlyShowCourseDescription,
                          });
                        }}
                      />
                      <FormGroup sx={{ marginLeft: "12px", flexGrow: 1 }}>
                        <FormControlLabel
                          disabled={!licenseGroup?.fallbackCourseDescription}
                          label={Translate.get("OnlyShowGenericCourseDescriptionTitle")}
                          sx={{ flexDirection: "row-reverse", justifyContent: "space-between", marginRight: "0px" }}
                          control={
                            <Switch
                              checked={licenseGroup.fallbackOnlyShowCourseDescription ?? false}
                              onChange={(event) =>
                                setLicenseGroup({
                                  ...licenseGroup,
                                  fallbackOnlyShowCourseDescription: event.target.checked,
                                })
                              }
                            />
                          }
                        />
                      </FormGroup>
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeAndReload}>{Translate.get("Cancel")}</Button>
            <ButtonWithSpinner
              disabled={!licenseGroup || !licenseGroup.name || !licenseGroup.webColor}
              onClick={handleSaveLicenseGroup}
            >
              {Translate.get("Save")}
            </ButtonWithSpinner>
          </DialogActions>
        </>
      )}
    </>
  );
}
